import React from 'react';
import ReactDOM from 'react-dom';
import { Button, StepItem, Steps, SvgIcon } from '@gik/ui';
import {
  BillingForm,
  BillingFormValues,
  CartItem,
  OrderForm,
  OrderSummary,
  ShippingDetails,
  ShippingForm,
} from '@gik/checkout';
import ProductList from '../ProductList/ProductList';
import { OrderSummaryValues } from '../OrderSummary/OrderSummary';
import { Source, StripeCardElement } from '@stripe/stripe-js';
import { FormApi } from 'final-form';
import { BillingDetails, CheckoutFormInitiatedOnType } from '@gik/checkout/types';
import { Analytics } from '@gik/core/utils';
import { IAnalyticsProps } from '@gik/core/utils/Analytics';
import { useUserStore } from '@gik/core/store/UserStore';
import { withStripe } from '@gik/ui/Stripe';
import { useStripe } from '@stripe/react-stripe-js';
import { createStripeSource } from '../BillingForm/BillingForm';
import { ICalendarEventClaimCreatePayload } from '@gik/api/calendar/calendarClaims';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

export const summaryFormId = 'summaryForm';
type CheckoutFormIds = 'orderForm' | 'billingForm' | 'shippingForm' | typeof summaryFormId;

export interface CheckoutFormProps {
  /**
   * Render Shipping information in order summary
   */
  shipping?: boolean;

  /**
   * Allow user to enter shipping details (disabled for purchasing for inkind pages)
   */
  shippingEditable?: boolean;

  /**
   * Allow a shipping option to be selected (standard, fast delivery)
   */
  hasShipping?: boolean;

  /**
   * Skip the products step when first opened (product/wishlist/giftbox page)
   */
  skipProducts?: boolean;

  /**
   * Hide the product step
   */
  hideProducts?: boolean;

  buttonsPortal?: HTMLElement;
  stepsPortal?: HTMLElement;
  stepsNavPortal?: HTMLElement;

  initialData?: CheckoutFormValues;

  inkindRouteId?: string;
  initiatedOn: CheckoutFormInitiatedOnType;

  errorPage?: React.ReactElement;

  /**
   * An error message to be displayed below the form.
   * Normally this would be used to display the error response from the backend.
   */
  errorMessage?: string;

  onSubmit?(
    values: CheckoutFormValues,
    updateCheckoutStatusText: (text: string) => void
  ): boolean | void | Promise<boolean> | Promise<void>;

  onChange?(values: CheckoutFormValues): void;

  onGotoStep?(stepId: string): void;
}

export interface CheckoutFormValues {
  cart?: CartItem[];
  billing?: BillingFormValues;
  shipping?: ShippingDetails;
  summary?: OrderSummaryValues;
}

export interface CheckoutFormPayload {
  stripeToken: string;
  timezoneOffset: number;
  inkindRouteId?: string;
  messageToRecipient?: string;
  tip?: number;
  total?: number; // used for verification only
  products?: CartItem[];
  billing?: BillingDetails;
  shipping?: ShippingDetails;
  summary?: OrderSummaryValues;
  // fields for events (not necessarily claiming if someone already claimed that day)
  eventType?: string;
  eventDateTime?: string;
  // fields for claiming as part of the order
  createClaimRequest?: ICalendarEventClaimCreatePayload;
  subscribeToNewsletter?: boolean;
  // frontend-generated transaction ID passed to backend to prevent double purchases
  frontendTransactionId: string;
}

export type CheckoutFormStepId = 'products' | 'billing' | 'payment' | 'shipping';

function CheckoutForm({
  initialData,
  skipProducts,
  hideProducts,
  shipping,
  shippingEditable,
  hasShipping,
  buttonsPortal,
  stepsPortal,
  stepsNavPortal,
  errorMessage,
  initiatedOn,
  inkindRouteId,
  errorPage,
  onSubmit,
  onChange,
  onGotoStep,
}: CheckoutFormProps): React.ReactElement {
  const [source, setSource] = React.useState(undefined);
  const [stripeError, setStripeError] = React.useState(null);
  const [cardElement, setCardElement] = React.useState<StripeCardElement>();
  const userStore = useUserStore();

  const stripe = useStripe();

  const steps: StepItem<CheckoutFormStepId>[] = [];

  if (!hideProducts) {
    steps.push({ id: 'products', name: 'INFO', prepend: <SvgIcon name="InformationCircleOutline" /> });
  }

  steps.push({ id: 'billing', name: 'BILLING', prepend: <SvgIcon name="CreditCardOutline" /> });

  if (shippingEditable) {
    steps.push({ id: 'shipping', name: 'SHIPPING', prepend: <SvgIcon name="TruckOutline" /> });
  }

  steps.push({ id: 'payment', name: 'CONFIRM & PAY', prepend: <SvgIcon name="ClipboardCheckOutline" /> });

  let firstStep: CheckoutFormStepId = 'products';
  if (skipProducts || hideProducts) {
    firstStep = 'billing';
  }

  const [stepIndex, setStepIndex] = React.useState<number>(0);

  // frontend-generated transaction ID passed to backend to prevent double purchases
  const [frontendTransactionId, setFrontendTransactionId] = useState<string>(null);
  const [data, setData] = React.useState(initialData);

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submittingText, setSubmittingText] = React.useState('');
  const [isOrderFormLoading, setIsOrderFormLoading] = React.useState(true);
  const [isOrderSummaryFormLoading, setIsOrderSummaryFormLoading] = React.useState(true);

  React.useEffect(() => {
    setFrontendTransactionId(uuidv4());
    gotoStep(firstStep);
  }, []);

  function setStep(stepId: CheckoutFormStepId) {
    const newStepIndex = steps.findIndex(step => step.id === stepId);
    setStepIndex(newStepIndex);
  }

  function gotoStep(stepId: CheckoutFormStepId) {
    setStep(stepId);

    const commonAnalyticProps: IAnalyticsProps = {
      productNames: data.cart?.map(item => item.name).join(','),
      productIds: data.cart?.map(item => item.productId).join(','),
      productCheckoutTypes: data.cart?.map(item => item.checkoutType).join(','),
      userId: userStore.id,
      initiatedOn,
      inkindRouteId,
    };

    switch (stepId) {
      case 'products':
        Analytics.fireEvent('LoadProductCheckout', {
          ...commonAnalyticProps,
        });
        break;
      case 'billing':
        Analytics.fireEvent('LoadBillingCheckout', {
          ...commonAnalyticProps,
        });
        break;
      case 'shipping':
        Analytics.fireEvent('LoadShippingCheckout', {
          ...commonAnalyticProps,
        });
        break;
      case 'payment':
        Analytics.fireEvent('LoadFinishCheckout', {
          ...commonAnalyticProps,
        });
        break;
      default:
        console.error('Unsupported step: ', stepId);
    }

    if (onGotoStep) {
      onGotoStep(stepId);
    }
  }

  function handleClickSteps(stepItem: StepItem<CheckoutFormStepId>) {
    gotoStep(stepItem.id);
  }

  function handleNext(id: CheckoutFormIds) {
    if (isSubmitting) {
      return;
    }
    document.getElementById(id).dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  }

  function gotoNextStep() {
    if (stepIndex < steps.length) {
      gotoStep(steps[stepIndex + 1].id);
    }
  }

  function handleStepProgression(newData: CheckoutFormValues) {
    setData(newData);
    if (onChange) {
      onChange(newData);
    }
    gotoNextStep();
  }

  function handleSubmitOrder(values: CartItem[]) {
    const newData = JSON.parse(JSON.stringify(data)) as CheckoutFormValues;
    newData.cart = values;

    handleStepProgression(newData);
  }

  function handleSubmitBilling(values: BillingFormValues) {
    const newData = JSON.parse(JSON.stringify(data)) as CheckoutFormValues;

    newData.billing = values;
    handleStepProgression(newData);
  }

  function handleAfterSubmitBilling(_values: BillingFormValues, _form: FormApi, source: Source) {
    if (source) {
      setSource(source);
    }
  }

  function handleSubmitShipping(values: ShippingDetails) {
    const newData = JSON.parse(JSON.stringify(data)) as CheckoutFormValues;
    newData.shipping = values;
    handleStepProgression(newData);
  }

  function updateSummaryData(values: OrderSummaryValues): CheckoutFormValues {
    const newData = JSON.parse(JSON.stringify(data)) as CheckoutFormValues;
    newData.summary = values;
    return newData;
  }

  function handleSubmitSummary(values: OrderSummaryValues) {
    const newData = updateSummaryData(values);
    handleComplete(newData);
  }

  function handleChangeSummary(values: OrderSummaryValues) {
    const newData = updateSummaryData(values);
    newData.shipping.shippingOptionName = values.shippingOptionName;
    setData(newData);
    if (onChange) {
      onChange(newData);
    }
  }

  function handleChangeTip(tipPercentage: number, tipAmount: number) {
    const newData = JSON.parse(JSON.stringify(data)) as CheckoutFormValues;
    newData.summary.tipAmount = tipAmount;
    newData.summary.tipPercentage = tipPercentage;
    setData(newData);
    if (onChange) {
      onChange(newData);
    }
  }

  async function handleComplete(data: CheckoutFormValues) {
    setIsSubmitting(true);
    setStripeError(null);

    // clone cartItems and update the price
    const newOrders = JSON.parse(JSON.stringify(data.cart)) as CartItem[];
    newOrders.map((cartItem: CartItem, index: number) => {
      cartItem.price = data.cart[index].price;
    });

    const { error, source } = await createStripeSource(data.billing, stripe, cardElement);

    if (error) {
      setStripeError(error);
      return;
    }

    // set new stripe token on billing values
    const stripeToken = source.id;

    // const stripeToken = data.billing.stripeToken;

    // create payload expected by the backend
    const payload: CheckoutFormPayload = {
      stripeToken,
      timezoneOffset: new Date().getTimezoneOffset(),
      messageToRecipient: data.summary.messageToRecipient,
      tip: data.summary.tipAmount,
      products: newOrders,
      billing: data.billing,
      total: data.summary.total,
      inkindRouteId,
      subscribeToNewsletter: data.summary.subscribeToNewsletter,
      frontendTransactionId,
    };

    // always add shipping data if it's available
    if (data.shipping) {
      payload.shipping = data.shipping;
    }

    if (onSubmit) {
      setSubmittingText('');
      const submitSuccess = await onSubmit(payload, progressText => {
        setSubmittingText(progressText);
      });
      // remove the stripe token after submitting the form (to reset the form entirely)
      if (submitSuccess) {
        delete data.billing.stripeToken;
      }
    }

    setIsSubmitting(false);
  }

  const buttons = [];
  const stepItem = steps[stepIndex];
  if (!stepItem) {
    return null;
  }
  const currentStepId = stepItem.id;
  const productsStepButtonText = initiatedOn === 'wishlist' ? 'BUY' : 'CONTINUE';

  switch (currentStepId) {
    case 'products':
      buttons.push(
        <Button
          wide
          key="btn-order-next"
          disabled={isOrderFormLoading}
          variant="primary"
          onClick={() => handleNext('orderForm')}
        >
          {productsStepButtonText}
        </Button>
      );
      break;
    case 'billing':
      buttons.push(
        <Button wide key="btn-billing-next" variant="primary" onClick={() => handleNext('billingForm')}>
          CONTINUE
        </Button>
      );
      break;
    case 'shipping':
      buttons.push(
        <Button wide key="btn-shipping-next" variant="primary" onClick={() => handleNext('shippingForm')}>
          CONTINUE
        </Button>
      );
      break;
    case 'payment':
      if (!errorPage) {
        buttons.push(
          <Button
            wide
            key="btn-summary-next"
            variant="primary"
            onClick={() => handleNext('summaryForm')}
            disabled={isOrderSummaryFormLoading}
            loading={isSubmitting}
          >
            {(isSubmitting ? submittingText : null) || 'COMPLETE PURCHASE'}
          </Button>
        );
      }
      break;
  }

  const isShippingStep = currentStepId === 'shipping';

  const recipientName = (data?.shipping?.firstName + ' ' + (data?.shipping?.lastName || '')).trim();
  const productList =
    data?.cart?.length > 0 ? (
      <ProductList
        cart={data?.cart}
        shipping={data?.shipping}
        recipientName={recipientName}
        isShippingStep={isShippingStep}
        inkindRouteId={inkindRouteId}
      />
    ) : null;

  const productIds = data.cart?.map(order => order.productId);

  return (
    <>
      {errorPage}
      <Steps
        variant="primary"
        allowBackwardNavigation
        steps={steps}
        index={stepIndex}
        onClick={handleClickSteps}
        navPortal={stepsNavPortal}
        portal={stepsPortal}
        renderHiddenTabs
        hidden={!!errorPage}
      >
        {!hideProducts && (
          <OrderForm
            id="orderForm"
            setIsOrderFormLoading={setIsOrderFormLoading}
            productIds={productIds}
            initialOrders={data?.cart}
            onSubmit={handleSubmitOrder}
          />
        )}
        <div>
          {productList}
          <span className="gik-form-header">Billing Info</span>
          <BillingForm
            id="billingForm"
            initialValues={data?.billing}
            initialOrders={data?.cart}
            shippingDetails={data?.shipping}
            onSubmit={handleSubmitBilling}
            onAfterSubmit={handleAfterSubmitBilling}
            setCardElement={setCardElement}
            creditCardLast4={source && source.card.last4}
          />
        </div>
        {shippingEditable && (
          <div>
            {productList}
            <span className="gik-form-header">Shipping Details</span>
            <ShippingForm id="shippingForm" initialValues={data?.shipping} onSubmit={handleSubmitShipping} />
          </div>
        )}
        <div>
          {productList}
          {currentStepId === 'payment' && (
            <OrderSummary
              id="summaryForm"
              initialValues={data?.summary}
              cart={data?.cart}
              shipping={shipping}
              hasShipping={hasShipping}
              shippingDetails={data?.shipping}
              setIsOrderSummaryFormLoading={setIsOrderSummaryFormLoading}
              onSubmit={handleSubmitSummary}
              onChange={handleChangeSummary}
              onTipChange={handleChangeTip}
              stripeErrorMessage={stripeError?.message}
              errorMessage={errorMessage}
            />
          )}
        </div>
      </Steps>

      {buttonsPortal && ReactDOM.createPortal(buttons, buttonsPortal)}
      {!buttonsPortal && buttons}
    </>
  );
}

export default withStripe(CheckoutForm);
